/*!
 * Spruce Hydration Quiz — vanilla web component for Shopify themes
 * v1.0.0
 * Posts step-level funnel events to /functions/v1/hydration-event and the
 * final profile to /functions/v1/hydration-submit.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('spruce-hydration-quiz')) return;

  // ── config ──────────────────────────────────────────────────────────────
  const ACTIVITY_MULT = { sitting: 1.15, feet: 1.25, workout: 1.45, daily_train: 1.65 };
  const CLIMATE_MULT = { cool: 1.05, mild: 1.10, hot: 1.20 };
  const ALCOHOL_MULT = { none: 1, '3to7': 1.15, '8to15': 1.25, over15: 1.40 };
  const COFFEE_ADD_L = { under4: 0, over4: 0.49 };

  const STEPS = [
    'goal', 'weight', 'activity', 'climate',
    'wee', 'birthday', 'email', 'result'
  ];

  // Psychological hooks shown above the question title
  const HOOKS = {
    goal: "Most people know they're dehydrated. Almost none know their actual number.",
    weight: "You've already done what only one in three starters did."
  };

  const Q = {
    goal: {
      title: "What's your main hydration goal?",
      hint: "Start with what matters most right now. We'll build your exact daily target and stack around it.",
      options: [
        { v: 'performance', label: 'Train & perform' },
        { v: 'energy', label: 'All-day energy' },
        { v: 'recovery', label: 'Better sleep & relaxing' },
        { v: 'daily', label: 'Just drink more water' }
      ]
    },
    activity: {
      title: 'How active are you in a typical week?',
      hint: 'The more you move, the more hydration you need.',
      options: [
        { v: 'sitting', label: 'Mostly sitting' },
        { v: 'feet', label: 'On my feet often' },
        { v: 'workout', label: 'Workout 4+ times a week' },
        { v: 'daily_train', label: 'Daily training' }
      ]
    },
    climate: {
      title: "What's the typical temperature where you spend most your time?",
      hint: 'Heat increases sweat loss, which means more water.',
      cols: 3,
      options: [
        { v: 'cool', label: 'Cool' },
        { v: 'mild', label: 'Mild' },
        { v: 'hot', label: 'Hot & humid' }
      ]
    },
    wee: {
      title: 'What was the colour of your last wee?',
      hint: 'Still the best way to read your current hydration.',
      options: [
        { v: 'clear', label: 'Colourless' },
        { v: 'straw', label: 'Straw-like yellow' },
        { v: 'dark', label: 'Dark yellow' },
        { v: 'skip', label: 'Keep the personal questions to yourself!' }
      ]
    }
  };

  const WEE_MSG = {
    clear: ['Great work, certified hydration pro.', 'Save some water for the rest of us.', 'Nice to meet a fellow professional water drinker.'],
    straw: ["Room for improvement, you've got this.", "It's giving 6 out of 10. We can do better.", "We'll make a good water drinker out of you yet."],
    dark: ['At least the only way is up.', 'Good thing we do next-day delivery, your body needs it.', 'Time to drink up. Water you waiting for?'],
    skip: ['Fair enough, your secret is safe with us.']
  };

  // handle = Shopify product handle on spruce-water.com
  function recommend(goal) {
    switch (goal) {
      case 'performance': return { products: [{ handle: 'lemon-and-pomegranate-electrolyte', name: 'Lemon & Pomegranate Electrolyte' }], title: 'The Performer', tagline: 'Train, sweat, replace. Electrolytes do the heavy lifting.', cluster: 'electrolytes', clusterLabel: 'electrolytes', clusterUrl: '/pages/electrolytes-uk' };
      case 'energy':      return { products: [{ handle: 'spruce-water-natural-water-enhancer-lime-and-mint', name: 'Lime & Mint with Ginseng' }], title: 'The All-Day Engine', tagline: 'Lime and mint with ginseng for steady, clean energy.', cluster: 'water-enhancer', clusterLabel: 'water enhancers', clusterUrl: '/pages/water-enhancer-uk' };
      case 'recovery':    return { products: [{ handle: 'spruce-water-spruce-peach-perfect-magnesium', name: 'Peach Perfect Magnesium' }], title: 'The Recovery Pro', tagline: 'Sleep deeper, wake calmer. Magnesium is the move.', cluster: 'magnesium', clusterLabel: 'magnesium drinks', clusterUrl: '/pages/magnesium-drink' };
      default:            return { products: [{ handle: '4-pack-bundle', name: 'Spruce 4 Pack Bundle' }], title: 'The Sipper', tagline: 'Make every glass count with real fruit and vitamins.', cluster: 'water-enhancer', clusterLabel: 'water flavouring', clusterUrl: '/pages/water-enhancer-uk' };
    }
  }

  function calculate(a) {
    let l = (Number(a.weight) || 0) * 0.033;
    if (a.activity) l *= ACTIVITY_MULT[a.activity];
    if (a.climate) l *= CLIMATE_MULT[a.climate];
    if (a.alcohol) l *= ALCOHOL_MULT[a.alcohol];
    if (a.coffee) l += COFFEE_ADD_L[a.coffee];
    const ml = Math.round(l * 1000);
    return { ml, litres: l.toFixed(2), glasses: Math.round(ml / 250) };
  }

  function uuid() {
    if (crypto && crypto.randomUUID) return crypto.randomUUID();
    return 'sx-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  // ── component ───────────────────────────────────────────────────────────
  class SpruceHydrationQuiz extends HTMLElement {
    constructor() {
      super();
      this.state = {
        stepIndex: 0,
        answers: {
          goal: null, gender: 'na', weight: 70,
          activity: null, climate: null, coffee: 'under4', alcohol: 'none', wee: null,
          birthday: '', email: '', consent: true
        },
        submitting: false, submitted: false, result: null
      };
    }

    connectedCallback() {
      this.apiBase = this.getAttribute('api-base') || 'https://hfxvznaahaiqnzjuvdlg.supabase.co/functions/v1';
      this.anonKey = this.getAttribute('anon-key') || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhmeHZ6bmFhaGFpcW56anV2ZGxnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg0OTc4MTksImV4cCI6MjA5NDA3MzgxOX0.ccy0IZWxwGfy1jIm-B9OyX3pdYUM-XojZl1hEcTvg6Y';
      this.shopifyDomain = this.getAttribute('shopify-domain') || (window.location ? window.location.hostname : '');
      this.customerEmail = this.getAttribute('customer-email') || '';
      if (this.customerEmail) this.state.answers.email = this.customerEmail;

      // Persistent session id
      try {
        const stored = localStorage.getItem('spruce_quiz_session');
        if (stored) {
          const obj = JSON.parse(stored);
          if (obj && obj.id && Date.now() - obj.ts < 1000 * 60 * 60 * 24 * 30) {
            this.sessionId = obj.id;
            this.sessionStart = obj.start || Date.now();
          }
        }
      } catch (_) {}
      if (!this.sessionId) {
        this.sessionId = uuid();
        this.sessionStart = Date.now();
      }
      this.persistSession();

      this.classList.add('shq');
      this.render();
      this.event('start', STEPS[0]);
      this.event('view', STEPS[0]);
    }

    persistSession() {
      try { localStorage.setItem('spruce_quiz_session', JSON.stringify({ id: this.sessionId, start: this.sessionStart, ts: Date.now() })); } catch (_) {}
    }

    event(type, step, extra) {
      const body = {
        session_id: this.sessionId,
        step: step,
        event_type: type,
        question_key: extra && extra.question_key,
        answer_value: extra && extra.answer_value,
        payload: (extra && extra.payload) || {},
        source_page: window.location.pathname,
        source_store: this.shopifyDomain,
        ms_since_session_start: Date.now() - this.sessionStart
      };
      try {
        fetch(this.apiBase + '/hydration-event', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', apikey: this.anonKey, Authorization: 'Bearer ' + this.anonKey },
          keepalive: true,
          body: JSON.stringify(body)
        }).catch(function () {});
      } catch (_) {}
    }

    set(k, v) { this.state.answers[k] = v; }

    next() {
      if (this.state.stepIndex < STEPS.length - 1) {
        this.state.stepIndex++;
        this.render();
        this.event('view', STEPS[this.state.stepIndex]);
      }
    }
    back() {
      if (this.state.stepIndex > 0) {
        const from = STEPS[this.state.stepIndex];
        this.state.stepIndex--;
        this.event('back', from, { payload: { to: STEPS[this.state.stepIndex] } });
        this.render();
      }
    }
    pickAndNext(k, v, label) {
      this.set(k, v);
      this.event('answer', STEPS[this.state.stepIndex], { question_key: k, answer_value: String(v), payload: { label: label } });
      const self = this;
      setTimeout(function () { self.next(); }, 180);
    }

    async submit() {
      const a = this.state.answers;
      const email = (a.email || '').trim().toLowerCase();
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        alert("That email doesn't look right.");
        return;
      }
      this.state.submitting = true;
      this.render();
      this.event('submit_attempt', 'email', { payload: { has_email: !!email } });
      const calc = calculate(a);
      try {
        if (email) {
          const res = await fetch(this.apiBase + '/hydration-submit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', apikey: this.anonKey, Authorization: 'Bearer ' + this.anonKey },
            body: JSON.stringify({
              email: email,
              weight_kg: a.weight,
              gender: a.gender,
              goal: a.goal,
              activity: a.activity,
              climate: a.climate,
              coffee: a.coffee,
              alcohol: a.alcohol,
              wee: a.wee,
              birthday: a.birthday || null,
              daily_target_ml: calc.ml,
              consent: a.consent,
              session_id: this.sessionId,
              source_page: window.location.pathname,
              source_store: this.shopifyDomain
            })
          });
          const data = await res.json().catch(function () { return {}; });
          if (!res.ok || data.error) throw new Error(data.error || ('http_' + res.status));
        }
        this.state.submitted = true;
        this.state.submitting = false;
        this.next();
      } catch (e) {
        console.error('quiz submit failed', e);
        this.event('submit_error', 'email', { payload: { message: String(e && e.message || e) } });
        this.state.submitting = false;
        this.render();
        alert("Couldn't save your plan. Try again in a moment.");
      }
    }

    reset() {
      this.state = {
        stepIndex: 0,
        answers: {
          goal: null, gender: 'na', weight: 70,
          activity: null, climate: null, coffee: 'under4', alcohol: 'none', wee: null,
          birthday: '', email: this.customerEmail || '', consent: true
        },
        submitting: false, submitted: false, result: null
      };
      this.sessionId = uuid();
      this.sessionStart = Date.now();
      this.persistSession();
      this.render();
      this.event('start', STEPS[0]);
      this.event('view', STEPS[0]);
    }

    render() {
      const idx = this.state.stepIndex;
      const step = STEPS[idx];
      const progress = Math.round((idx / (STEPS.length - 1)) * 100);
      const a = this.state.answers;

      let body = '';
      if (step === 'goal' || step === 'activity' || step === 'climate' || step === 'wee') {
        body = this.renderTiles(step);
      } else if (step === 'weight') {
        body = this.renderQuiz('How much do you weigh?', "Your bodyweight sets your baseline number. We'll calibrate for activity next.",
          '<div class="shq-weight"><input type="number" min="30" max="300" value="' + Number(a.weight) + '" class="shq-input shq-input-large" data-shq-input="weight" /><span class="shq-unit">KG</span></div>', step);
      } else if (step === 'birthday') {
        body = this.renderQuiz("When's your birthday?", 'So we never nag you about hydration on your big day.',
          '<input type="date" value="' + (a.birthday || '') + '" class="shq-input" data-shq-input="birthday" />', step);
      } else if (step === 'email') {
        body = this.renderQuiz('Where should we send your plan?', 'Get your personalised target plus HYDRATE20 for 20% off your first order.',
          '<input type="email" placeholder="you@hydrated.com" value="' + (a.email || '').replace(/"/g, '&quot;') + '" class="shq-input" data-shq-input="email" />' +
          '<label class="shq-consent"><input type="checkbox" ' + (a.consent ? 'checked' : '') + ' data-shq-consent /> Send me hydration tips and Spruce offers. Unsubscribe anytime.</label>' +
          '<button class="shq-cta" data-shq-submit ' + (this.state.submitting ? 'disabled' : '') + '>' + (this.state.submitting ? 'Crunching the numbers\u2026' : 'Reveal my plan \u2192') + '</button>', step
        );
      } else if (step === 'result') {
        body = this.renderResult();
      }

      this.innerHTML =
        '<div class="shq-wrap">' +
          '<div class="shq-progress"><div class="shq-progress-meta"><span>Question ' + Math.min(idx + 1, STEPS.length - 1) + ' / ' + (STEPS.length - 1) + '</span><span>' + progress + '%</span></div>' +
            '<div class="shq-bar"><div class="shq-bar-fill" style="width:' + progress + '%"></div></div>' +
          '</div>' +
          '<div class="shq-card">' + body +
            (step !== 'result' ? '<div class="shq-nav"><button class="shq-back" data-shq-back ' + (idx === 0 ? 'disabled' : '') + '>\u2190 Back</button>' +
              ((step === 'weight' || step === 'birthday') ? '<button class="shq-next" data-shq-next>' + (step === 'birthday' ? 'Skip \u2192' : 'Next \u2192') + '</button>' : '<span></span>') +
              '</div>' : '') +
          '</div>' +
        '</div>';

      this.bindEvents();
    }

    renderHook(stepKey) {
      const hook = HOOKS[stepKey];
      return hook ? '<p class="shq-hook">' + hook + '</p>' : '';
    }

    renderQuiz(title, hint, inner, stepKey) {
      return this.renderHook(stepKey) + '<h3 class="shq-title">' + title + '</h3><p class="shq-hint">' + hint + '</p><div class="shq-body shq-center">' + inner + '</div>';
    }

    renderTiles(stepKey) {
      const cfg = Q[stepKey];
      const cur = this.state.answers[stepKey];
      const cols = cfg.cols || 2;
      const tiles = cfg.options.map(function (o) {
        const sel = cur === o.v ? ' is-selected' : '';
        return '<button class="shq-tile' + sel + '" data-shq-pick="' + stepKey + '" data-v="' + o.v + '" data-label="' + o.label.replace(/"/g, '&quot;') + '"><span>' + o.label + '</span></button>';
      }).join('');
      return this.renderHook(stepKey) + '<h3 class="shq-title">' + cfg.title + '</h3><p class="shq-hint">' + cfg.hint + '</p><div class="shq-grid shq-grid-' + cols + '">' + tiles + '</div>';
    }

    renderResult() {
      const a = this.state.answers;
      const calc = calculate(a);
      const r = recommend(a.goal);
      const wee = a.wee || 'skip';
      const msgs = WEE_MSG[wee] || WEE_MSG.skip;
      const weeMsg = msgs[Math.floor(Math.random() * msgs.length)];

      const products = r.products.map(function (p) {
        return '<a class="shq-product" href="/products/' + p.handle + '" data-shq-prod="' + p.handle + '">' +
                 '<div class="shq-product-img" data-shq-img>' +
                   '<div class="shq-product-img-skeleton"></div>' +
                 '</div>' +
                 '<div class="shq-product-meta">' +
                   '<div class="shq-product-name" data-shq-name>' + p.name + '</div>' +
                   '<div class="shq-product-price" data-shq-price>\u2014</div>' +
                 '</div>' +
                 '<span class="shq-product-arrow" aria-hidden="true">\u2192</span>' +
               '</a>';
      }).join('');

      // Spruce Bottle upsell, sized to their daily target (500ml bottle)
      const BOTTLE_ML = 500;
      const bottlesPerDay = Math.max(1, Math.round((calc.ml || 0) / BOTTLE_ML));
      const bottleUpsell =
        '<a class="shq-bottle-upsell" href="/products/spruce-water-bottle" data-shq-prod="spruce-water-bottle" data-shq-bottle>' +
          '<div class="shq-bottle-img" data-shq-img>' +
            '<div class="shq-product-img-skeleton"></div>' +
          '</div>' +
          '<div class="shq-bottle-body">' +
            '<div class="shq-bottle-eyebrow">Hit your target</div>' +
            '<div class="shq-bottle-title" data-shq-name>Spruce Water Bottle \u00B7 500ml</div>' +
            '<div class="shq-bottle-pitch">' +
              '<strong>' + bottlesPerDay + ' bottle' + (bottlesPerDay === 1 ? '' : 's') + ' a day</strong> and you\u2019re sprucing right. Recycled plastic, BPA-free, leak-free, made for the gym bag, the desk, the commute.' +
            '</div>' +
            '<div class="shq-bottle-meta">' +
              '<span class="shq-bottle-price" data-shq-price>\u2014</span>' +
              '<span class="shq-bottle-cta">Add the bottle \u2192</span>' +
            '</div>' +
          '</div>' +
        '</a>';

      return '<div class="shq-result">' +
        '<div class="shq-eyebrow">Your hydration profile</div>' +
        '<h3 class="shq-result-title">You\'re ' + r.title + '</h3>' +
        '<p class="shq-tagline">' + r.tagline + '</p>' +
        '<p class="shq-wee">' + weeMsg + '</p>' +
        '<div class="shq-target"><div class="shq-target-num">' + calc.litres + '</div><div class="shq-target-unit">litres per day</div><div class="shq-target-sub">~ ' + calc.glasses + ' glasses \u00B7 ' + calc.ml + ' ml</div></div>' +
        '<div class="shq-stack-label">Your Spruce stack</div>' +
        '<div class="shq-stack">' + products + '</div>' +
        bottleUpsell +
        (this.state.submitted ? '<div class="shq-promo">Use code <strong>HYDRATE20</strong> at checkout for 20% off your first order. Free shipping over \u00A330.</div>' : '') +
        '<a class="shq-cta" data-shq-shop href="/discount/HYDRATE20?redirect=/cart">Shop my stack' + (this.state.submitted ? ' \u00B7 HYDRATE20' : '') + '</a>' +
        '<a class="shq-link-pillar" href="' + r.clusterUrl + '" data-shq-cluster="' + r.cluster + '">Learn more about ' + r.clusterLabel + ' \u2192</a>' +
        '<p class="shq-disclaimer">This is not professional health advice. If you have serious concerns or existing medical conditions, please consult your doctor.</p>' +
        '<button class="shq-link" data-shq-reset>Retake the quiz</button>' +
        '</div>';
    }

    bindEvents() {
      const self = this;
      this.querySelectorAll('[data-shq-pick]').forEach(function (el) {
        el.addEventListener('click', function () {
          const k = el.getAttribute('data-shq-pick');
          const v = el.getAttribute('data-v');
          const label = el.getAttribute('data-label');
          self.pickAndNext(k, v, label);
        });
      });
      const backBtn = this.querySelector('[data-shq-back]');
      if (backBtn) backBtn.addEventListener('click', function () { self.back(); });
      const nextBtn = this.querySelector('[data-shq-next]');
      if (nextBtn) nextBtn.addEventListener('click', function () {
        const step = STEPS[self.state.stepIndex];
        const v = self.state.answers[step];
        self.event('answer', step, { question_key: step, answer_value: String(v) });
        self.next();
      });
      const inputs = this.querySelectorAll('[data-shq-input]');
      inputs.forEach(function (el) {
        el.addEventListener('input', function () {
          const k = el.getAttribute('data-shq-input');
          self.set(k, el.type === 'number' ? Number(el.value) : el.value);
        });
      });
      const consent = this.querySelector('[data-shq-consent]');
      if (consent) consent.addEventListener('change', function () { self.set('consent', consent.checked); });
      const submitBtn = this.querySelector('[data-shq-submit]');
      if (submitBtn) submitBtn.addEventListener('click', function () { self.submit(); });
      const skipBtn = this.querySelector('[data-shq-skip]');
      if (skipBtn) skipBtn.addEventListener('click', function () { self.state.submitted = false; self.next(); });
      const resetBtn = this.querySelector('[data-shq-reset]');
      if (resetBtn) resetBtn.addEventListener('click', function () { self.reset(); });

      // Hydrate product cards (image + price + variant id) from Shopify
      const cards = this.querySelectorAll('[data-shq-prod]');
      if (cards.length) {
        const variantIds = [];
        let pending = cards.length;
        cards.forEach(function (card) {
          const handle = card.getAttribute('data-shq-prod');
          fetch('/products/' + handle + '.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.ok ? r.json() : null; })
            .then(function (p) {
              const imgWrap0 = card.querySelector('[data-shq-img]');
              const priceEl0 = card.querySelector('[data-shq-price]');
              if (!p) {
                // Graceful fallback: hide skeleton + price line, keep name
                if (imgWrap0) imgWrap0.innerHTML = '<div class="shq-product-img-fallback">\u2728</div>';
                if (priceEl0) priceEl0.style.display = 'none';
                return;
              }
              const imgWrap = card.querySelector('[data-shq-img]');
              const nameEl = card.querySelector('[data-shq-name]');
              const priceEl = card.querySelector('[data-shq-price]');
              const featured = (p.featured_image || (p.images && p.images[0]) || '').replace(/^https?:/, '');
              if (featured && imgWrap) {
                imgWrap.innerHTML = '<img src="' + featured + '" alt="' + (p.title || '').replace(/"/g, '&quot;') + '" loading="lazy" />';
              }
              if (nameEl && p.title) nameEl.textContent = p.title;
              if (priceEl && typeof p.price === 'number') {
                priceEl.textContent = '\u00A3' + (p.price / 100).toFixed(2);
              }
              const vid = p.variants && p.variants[0] && p.variants[0].id;
              if (vid) variantIds.push(vid);
            })
            .catch(function () {})
            .finally(function () {
              pending--;
              if (pending === 0 && variantIds.length) {
                const cta = self.querySelector('[data-shq-shop]');
                if (cta) {
                  // Permalink: /cart/{vid}:1,{vid}:1?discount=HYDRATE20
                  const items = variantIds.map(function (id) { return id + ':1'; }).join(',');
                  cta.setAttribute('href', '/cart/' + items + '?discount=HYDRATE20');
                }
              }
            });
        });
      }
    }
  }

  customElements.define('spruce-hydration-quiz', SpruceHydrationQuiz);
})();
